import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, getToken, setToken, clearToken, authHeaders, fmtErr, downloadCsv, setMeta } from "./api";
import { T } from "./ui";

const emptyForm = (fields) => Object.fromEntries(fields.map((f) => [f.name, f.type === "checkbox" ? false : (f.default ?? "")]));

function FieldGrid({ fields, form, setForm, testidPrefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {fields.map((f) => {
        const val = form[f.name];
        const set = (v) => setForm({ ...form, [f.name]: v });
        const tid = `${testidPrefix}-${f.name}`;
        if (f.type === "checkbox") {
          return (
            <label key={f.name} className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid={tid} checked={!!val} onChange={(e) => set(e.target.checked)} className="w-4 h-4 accent-[#2d4a3e]" />
              <span className="text-sm text-ink dark:text-whisper">{f.label}</span>
            </label>
          );
        }
        return (
          <label key={f.name} className={`block ${f.type === "textarea" ? "sm:col-span-2" : ""}`}>
            <span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">{f.label}</span>
            {f.type === "textarea" ? (
              <textarea data-testid={tid} rows={f.rows || 3} className={T.input} value={val ?? ""} onChange={(e) => set(e.target.value)} />
            ) : f.type === "select" ? (
              <select data-testid={tid} className={T.input} value={val ?? ""} onChange={(e) => set(e.target.value)}>
                {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
              </select>
            ) : (
              <input data-testid={tid} type={f.type || "text"} className={T.input} value={val ?? ""} onChange={(e) => set(e.target.value)} />
            )}
          </label>
        );
      })}
    </div>
  );
}

function Login({ onOk }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      setToken(data.token);
      onOk(data.token);
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="tournee-admin-login" className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className={`${T.card} w-full max-w-sm space-y-4`}>
        <h1 className="font-serif text-2xl text-ink dark:text-whisper text-center">Administration — La Tournée</h1>
        <input data-testid="admin-email" type="email" required placeholder="Email" className={T.input} value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="admin-password" type="password" required placeholder="Mot de passe" className={T.input} value={password} onChange={(e) => setPassword(e.target.value)} />
        {err && <p data-testid="admin-login-error" className="text-sm text-red-700 dark:text-red-400">{err}</p>}
        <button data-testid="admin-login-submit" type="submit" disabled={busy} className={`${T.forest} w-full`}>{busy ? "Connexion…" : "Se connecter"}</button>
        <Link to="/tournee" className="block text-center text-xs text-stone dark:text-mist hover:text-gold">← Retour à la page tournée</Link>
      </form>
    </div>
  );
}

// ---------- Onglets listes (demandes / lieux / inscriptions) ----------
const STATUS_LABELS = { nouvelle: "Nouvelle", en_cours: "À l'étude", retenue: "Retenue", archivee: "Archivée" };
const INTERET_LABELS = { soiree: "Soirée (vendredi)", soin: "Soin individuel (samedi)", journee: "Journée (dimanche)", les_deux: "Tout le week-end", formation: "Formation" };

function ListTab({ path, columns, csvName, withNotes = false, testid }) {
  const [items, setItems] = useState([]);
  const [ville, setVille] = useState("");
  const [statut, setStatut] = useState("");
  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (ville) params.set("ville", ville);
    if (statut) params.set("statut", statut);
    axios.get(`${API}/admin/${path}?${params}`, { headers: authHeaders() }).then((r) => setItems(r.data.items)).catch(() => {});
  }, [path, ville, statut]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, payload) => {
    await axios.patch(`${API}/admin/${path}/${id}`, payload, { headers: authHeaders() });
    load();
  };

  // Regroupement par ville
  const byVille = items.reduce((acc, it) => { const k = `${it.ville} (${it.pays})`; acc[k] = (acc[k] || 0) + 1; return acc; }, {});

  return (
    <div data-testid={testid} className="space-y-5">
      <div className="flex flex-wrap gap-3 items-center">
        <input data-testid={`${testid}-filter-ville`} placeholder="Filtrer par ville" className={`${T.input} !w-48`} value={ville} onChange={(e) => setVille(e.target.value)} />
        <select data-testid={`${testid}-filter-statut`} className={`${T.input} !w-44`} value={statut} onChange={(e) => setStatut(e.target.value)}>
          <option value="">Tous statuts</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button data-testid={`${testid}-csv`} onClick={() => downloadCsv(`${API}/admin/${path}.csv`, csvName)} className={T.outline}>Export CSV</button>
      </div>
      {Object.keys(byVille).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(byVille).map(([k, n]) => (
            <span key={k} className="text-[10px] tracking-[0.12em] uppercase border border-beige dark:border-charcoal rounded-full px-3 py-1.5 text-stone dark:text-mist">{k} — {n}</span>
          ))}
        </div>
      )}
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-stone dark:text-mist">Aucun élément pour l'instant.</p>}
        {items.map((it, i) => (
          <div key={it.id} data-testid={`${testid}-row-${i}`} className={`${T.card} !p-5`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-ink dark:text-whisper">{it.prenom} {it.nom} <span className="text-xs text-stone dark:text-mist">· {it.email}{it.telephone ? ` · ${it.telephone}` : ""}</span></p>
                <p className="text-sm text-stone dark:text-mist mt-1">
                  {it.ville} ({it.pays}){it.interet ? ` — ${INTERET_LABELS[it.interet] || it.interet}` : ""}{it.nom_lieu ? ` — ${it.nom_lieu} (${it.type_lieu})` : ""}{it.capacite ? ` — ${it.capacite}` : ""}
                </p>
                {(it.commentaires || it.disponibilites) && <p className="text-xs text-stone/80 dark:text-mist/80 mt-1">{it.disponibilites} {it.commentaires}</p>}
                {it.offre_titre && <p className="text-xs text-stone dark:text-mist mt-1">Offre : {it.offre_titre}</p>}
                <p className="text-[10px] text-stone/60 dark:text-mist/60 mt-1">{new Date(it.created_at).toLocaleString("fr-FR")}</p>
              </div>
              <select data-testid={`${testid}-status-${i}`} className={`${T.input} !w-36 !py-2 text-xs`} value={it.status} onChange={(e) => patch(it.id, { status: e.target.value })}>
                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {withNotes && (
              <div className="mt-3 flex gap-2">
                <input data-testid={`${testid}-notes-${i}`} placeholder="Notes internes…" className={T.input} defaultValue={it.notes || ""} onBlur={(e) => e.target.value !== (it.notes || "") && patch(it.id, { notes: e.target.value })} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- CRUD générique (programme / vidéos / témoignages / FAQ) ----------
function CrudTab({ path, title, fields, withGlobal = false, testid }) {
  const [villes, setVilles] = useState([]);
  const [villeId, setVilleId] = useState(withGlobal ? "global" : "");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm(fields));
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    axios.get(`${API}/admin/villes`, { headers: authHeaders() }).then((r) => {
      setVilles(r.data.items);
      if (!withGlobal && r.data.items.length > 0) setVilleId((v) => v || r.data.items[0].id);
    }).catch(() => {});
  }, [withGlobal]);

  const load = useCallback(() => {
    if (!withGlobal && !villeId) { setItems([]); return; }
    axios.get(`${API}/admin/${path}?ville_id=${villeId || "global"}`, { headers: authHeaders() })
      .then((r) => setItems(r.data.items)).catch(() => {});
  }, [path, villeId, withGlobal]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    const payload = { ...form, ville_id: withGlobal && villeId === "global" ? null : villeId };
    try {
      if (editId) await axios.put(`${API}/admin/${path}/${editId}`, payload, { headers: authHeaders() });
      else await axios.post(`${API}/admin/${path}`, payload, { headers: authHeaders() });
      setForm(emptyForm(fields)); setEditId(null); load();
      setMsg({ type: "ok", text: editId ? "Modifié." : "Ajouté." });
    } catch (e2) {
      setMsg({ type: "err", text: fmtErr(e2) });
    }
  };

  return (
    <div data-testid={testid} className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-[11px] tracking-[0.18em] uppercase text-stone dark:text-mist">Ville :</label>
        <select data-testid={`${testid}-ville`} className={`${T.input} !w-56`} value={villeId} onChange={(e) => setVilleId(e.target.value)}>
          {withGlobal && <option value="global">Page tournée (globale)</option>}
          {villes.map((v) => <option key={v.id} value={v.id}>{v.nom} ({v.pays})</option>)}
        </select>
      </div>
      <form onSubmit={save} className={`${T.card} space-y-4`}>
        <p className="font-serif text-xl text-ink dark:text-whisper">{editId ? "Modifier" : "Ajouter"} — {title}</p>
        <FieldGrid fields={fields} form={form} setForm={setForm} testidPrefix={`${testid}-field`} />
        {msg.text && <p data-testid={`${testid}-msg`} className={`text-sm ${msg.type === "err" ? "text-red-700 dark:text-red-400" : "text-forest dark:text-gold"}`}>{msg.text}</p>}
        <div className="flex gap-3">
          <button data-testid={`${testid}-save`} type="submit" className={T.forest}>{editId ? "Enregistrer" : "Ajouter"}</button>
          {editId && <button type="button" onClick={() => { setEditId(null); setForm(emptyForm(fields)); }} className={T.outline}>Annuler</button>}
        </div>
      </form>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`${testid}-row-${i}`} className={`${T.card} !p-4 flex flex-wrap items-center justify-between gap-3 ${it.active === false ? "opacity-50" : ""}`}>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-ink dark:text-whisper truncate">{it.titre || it.question || it.auteur} {it.heure ? <span className="text-xs text-stone dark:text-mist">· {it.heure}</span> : ""}</p>
              <p className="text-xs text-stone dark:text-mist truncate">{it.description || it.reponse || it.texte || it.youtube_url}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`${testid}-toggle-${i}`} onClick={async () => { await axios.put(`${API}/admin/${path}/${it.id}`, { ...it, active: it.active === false }, { headers: authHeaders() }); load(); }} className={`${T.outline} !px-4 !py-2`}>{it.active === false ? "Activer" : "Masquer"}</button>
              <button data-testid={`${testid}-edit-${i}`} onClick={() => { setEditId(it.id); setForm({ ...emptyForm(fields), ...it }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className={`${T.outline} !px-4 !py-2`}>Modifier</button>
              <button data-testid={`${testid}-delete-${i}`} onClick={async () => { if (window.confirm("Supprimer définitivement ?")) { await axios.delete(`${API}/admin/${path}/${it.id}`, { headers: authHeaders() }); load(); } }} className={`${T.outline} !px-4 !py-2 !border-red-300 !text-red-700 dark:!text-red-400`}>Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Villes ----------
const VILLE_FORM_FIELDS = [
  { name: "nom", label: "Nom de la ville" }, { name: "pays", label: "Pays" },
  { name: "slug", label: "Slug (URL)" }, { name: "statut", label: "Statut affiché", default: "bientôt" },
  { name: "dates", label: "Dates affichées (texte)" }, { name: "date_debut", label: "Date de début (pour le rappel automatique la veille)", type: "date" },
  { name: "image", label: "Image (URL)" },
  { name: "lieu_nom", label: "Nom du lieu" }, { name: "adresse", label: "Adresse complète" },
  { name: "maps_url", label: "Lien Google Maps", type: "url" }, { name: "tidycal_url", label: "Lien Tidycal (spécifique ville)", type: "url" },
  { name: "acces", label: "Accès", type: "textarea", rows: 2 }, { name: "parking", label: "Parking", type: "textarea", rows: 2 },
  { name: "a_apporter", label: "À apporter", type: "textarea", rows: 2 }, { name: "annulation", label: "Conditions d'annulation", type: "textarea", rows: 2 },
  { name: "contact", label: "Contact sur place" },
  { name: "texte_pourquoi", label: "Texte « Pourquoi cette étape »", type: "textarea" },
  { name: "texte_pour_qui", label: "Texte « Pour qui ? »", type: "textarea" },
  { name: "texte_repars", label: "Texte « Ce que tu repars avec »", type: "textarea" },
  { name: "active", label: "Étape publiée (visible en ligne)", type: "checkbox" },
];

function VillesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm(VILLE_FORM_FIELDS));
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const load = useCallback(() => {
    axios.get(`${API}/admin/villes`, { headers: authHeaders() }).then((r) => setItems(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    try {
      if (editId) await axios.put(`${API}/admin/villes/${editId}`, form, { headers: authHeaders() });
      else await axios.post(`${API}/admin/villes`, form, { headers: authHeaders() });
      setForm(emptyForm(VILLE_FORM_FIELDS)); setEditId(null); load();
      setMsg({ type: "ok", text: editId ? "Ville mise à jour." : "Ville créée avec ses 3 offres par défaut." });
    } catch (e2) { setMsg({ type: "err", text: fmtErr(e2) }); }
  };

  const action = async (fn, okMsg) => { await fn(); load(); if (okMsg) setMsg({ type: "ok", text: okMsg }); };

  return (
    <div data-testid="admin-villes" className="space-y-6">
      <form onSubmit={save} className={`${T.card} space-y-4`}>
        <p className="font-serif text-xl text-ink dark:text-whisper">{editId ? "Modifier la ville" : "Créer une ville"}</p>
        <FieldGrid fields={VILLE_FORM_FIELDS} form={form} setForm={setForm} testidPrefix="ville-field" />
        {msg.text && <p data-testid="villes-msg" className={`text-sm ${msg.type === "err" ? "text-red-700 dark:text-red-400" : "text-forest dark:text-gold"}`}>{msg.text}</p>}
        <div className="flex gap-3">
          <button data-testid="ville-save" type="submit" className={T.forest}>{editId ? "Enregistrer" : "Créer la ville"}</button>
          {editId && <button type="button" onClick={() => { setEditId(null); setForm(emptyForm(VILLE_FORM_FIELDS)); }} className={T.outline}>Annuler</button>}
        </div>
      </form>
      <div className="space-y-3">
        {items.map((v, i) => (
          <div key={v.id} data-testid={`ville-row-${i}`} className={`${T.card} !p-5 ${!v.active ? "opacity-60" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-serif text-xl text-ink dark:text-whisper">{v.nom} <span className="text-sm text-stone dark:text-mist">· {v.pays} · /tournee/{v.slug}</span></p>
                <p className="text-xs text-stone dark:text-mist mt-1">{v.dates || "Dates à définir"} — {v.active ? "Publiée" : "Masquée"}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {v.active && <a data-testid={`ville-view-${i}`} href={`/tournee/${v.slug}`} target="_blank" rel="noopener noreferrer" className={`${T.outline} !px-4 !py-2`}>Voir</a>}
                <button data-testid={`ville-edit-${i}`} onClick={() => { setEditId(v.id); setForm({ ...emptyForm(VILLE_FORM_FIELDS), ...v }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className={`${T.outline} !px-4 !py-2`}>Modifier</button>
                <button data-testid={`ville-duplicate-${i}`} onClick={() => action(() => axios.post(`${API}/admin/villes/${v.id}/duplicate`, {}, { headers: authHeaders() }), "Ville dupliquée (inactive).")} className={`${T.outline} !px-4 !py-2`}>Dupliquer</button>
                <button data-testid={`ville-toggle-${i}`} onClick={() => action(() => axios.put(`${API}/admin/villes/${v.id}`, { active: !v.active }, { headers: authHeaders() }))} className={`${T.outline} !px-4 !py-2`}>{v.active ? "Désactiver" : "Activer"}</button>
                <button data-testid={`ville-reminder-${i}`} onClick={() => action(async () => { const r = await axios.post(`${API}/admin/villes/${v.id}/send-reminder`, {}, { headers: authHeaders() }); setMsg({ type: "ok", text: `Rappel envoyé à ${r.data.sent} inscrit(s).` }); })} className={`${T.outline} !px-4 !py-2`}>Rappel email</button>
                <button data-testid={`ville-post-${i}`} onClick={() => action(async () => { const r = await axios.post(`${API}/admin/villes/${v.id}/send-post-event`, {}, { headers: authHeaders() }); setMsg({ type: "ok", text: `Email post-événement envoyé à ${r.data.sent} inscrit(s).` }); })} className={`${T.outline} !px-4 !py-2`}>Email post-événement</button>
                <button data-testid={`ville-delete-${i}`} onClick={async () => { if (window.confirm("Supprimer la ville et tout son contenu ?")) action(() => axios.delete(`${API}/admin/villes/${v.id}`, { headers: authHeaders() })); }} className={`${T.outline} !px-4 !py-2 !border-red-300 !text-red-700 dark:!text-red-400`}>Supprimer</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Offres ----------
const OFFRE_FIELDS = [
  { name: "jour", label: "Jour", type: "select", options: ["vendredi", "samedi", "dimanche", "week-end", "autre"], default: "vendredi" },
  { name: "titre", label: "Titre" },
  { name: "description", label: "Description", type: "textarea", rows: 2 },
  { name: "horaire", label: "Horaire" }, { name: "duree", label: "Durée" },
  { name: "prix", label: "Prix (ex : 30 €)" }, { name: "early_bird", label: "Prix early bird (optionnel)" },
  { name: "capacite", label: "Capacité (places)" }, { name: "places_restantes", label: "Places restantes" },
  { name: "adresse", label: "Adresse (si différente)" }, { name: "badge", label: "Badge (ex : Découverte)" },
  { name: "ordre", label: "Ordre", type: "number", default: "1" },
  { name: "tidycal_url", label: "Lien Tidycal spécifique (optionnel)", type: "url" },
  { name: "prix30", label: "Soin — prix 30 min (optionnel)" },
  { name: "prix45", label: "Soin — prix 45 min (optionnel)" },
  { name: "prix60", label: "Soin — prix 60 min (optionnel)" },
  { name: "active", label: "Offre active (visible)", type: "checkbox", default: true },
];

function OffresTab() {
  const [villes, setVilles] = useState([]);
  const [villeId, setVilleId] = useState("");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm(OFFRE_FIELDS));
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    axios.get(`${API}/admin/villes`, { headers: authHeaders() }).then((r) => {
      setVilles(r.data.items);
      if (r.data.items.length > 0) setVilleId((v) => v || r.data.items[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!villeId) { setItems([]); return; }
    axios.get(`${API}/admin/offres?ville_id=${villeId}`, { headers: authHeaders() }).then((r) => setItems(r.data.items)).catch(() => {});
  }, [villeId]);
  useEffect(() => { load(); }, [load]);

  const toPayload = (f) => {
    const { prix30, prix45, prix60, ...rest } = f;
    const pd = {};
    if (prix30) pd["30"] = prix30;
    if (prix45) pd["45"] = prix45;
    if (prix60) pd["60"] = prix60;
    return { ...rest, prix_durees: pd, ville_id: villeId, ordre: Number(f.ordre) || 1 };
  };
  const fromItem = (it) => ({ ...emptyForm(OFFRE_FIELDS), ...it, prix30: it.prix_durees?.["30"] || "", prix45: it.prix_durees?.["45"] || "", prix60: it.prix_durees?.["60"] || "" });

  const save = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    try {
      if (editId) await axios.put(`${API}/admin/offres/${editId}`, toPayload(form), { headers: authHeaders() });
      else await axios.post(`${API}/admin/offres`, toPayload(form), { headers: authHeaders() });
      setForm(emptyForm(OFFRE_FIELDS)); setEditId(null); load();
      setMsg({ type: "ok", text: "Offre enregistrée." });
    } catch (e2) { setMsg({ type: "err", text: fmtErr(e2) }); }
  };

  return (
    <div data-testid="admin-offres" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-[11px] tracking-[0.18em] uppercase text-stone dark:text-mist">Ville :</label>
        <select data-testid="offres-ville" className={`${T.input} !w-56`} value={villeId} onChange={(e) => setVilleId(e.target.value)}>
          {villes.map((v) => <option key={v.id} value={v.id}>{v.nom} ({v.pays})</option>)}
        </select>
      </div>
      <form onSubmit={save} className={`${T.card} space-y-4`}>
        <p className="font-serif text-xl text-ink dark:text-whisper">{editId ? "Modifier l'offre" : "Créer une offre"}</p>
        <FieldGrid fields={OFFRE_FIELDS} form={form} setForm={setForm} testidPrefix="offre-field" />
        {msg.text && <p data-testid="offres-msg" className={`text-sm ${msg.type === "err" ? "text-red-700 dark:text-red-400" : "text-forest dark:text-gold"}`}>{msg.text}</p>}
        <div className="flex gap-3">
          <button data-testid="offre-save" type="submit" className={T.forest}>{editId ? "Enregistrer" : "Créer l'offre"}</button>
          {editId && <button type="button" onClick={() => { setEditId(null); setForm(emptyForm(OFFRE_FIELDS)); }} className={T.outline}>Annuler</button>}
        </div>
      </form>
      <div className="space-y-3">
        {items.map((o, i) => (
          <div key={o.id} data-testid={`offre-row-${i}`} className={`${T.card} !p-4 flex flex-wrap items-center justify-between gap-3 ${!o.active ? "opacity-50" : ""}`}>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-ink dark:text-whisper"><span className="text-[10px] tracking-[0.2em] uppercase text-gold mr-2">{o.jour}</span>{o.titre}</p>
              <p className="text-xs text-stone dark:text-mist">{o.prix}{o.early_bird ? ` (early bird ${o.early_bird})` : ""}{String(o.places_restantes) !== "" && o.places_restantes != null ? ` — ${o.places_restantes} place(s) restante(s)` : ""}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid={`offre-toggle-${i}`} onClick={async () => { await axios.put(`${API}/admin/offres/${o.id}`, { ...o, active: !o.active }, { headers: authHeaders() }); load(); }} className={`${T.outline} !px-4 !py-2`}>{o.active ? "Masquer" : "Activer"}</button>
              <button data-testid={`offre-duplicate-${i}`} onClick={async () => { await axios.post(`${API}/admin/offres/${o.id}/duplicate`, {}, { headers: authHeaders() }); load(); }} className={`${T.outline} !px-4 !py-2`}>Dupliquer</button>
              <button data-testid={`offre-edit-${i}`} onClick={() => { setEditId(o.id); setForm(fromItem(o)); window.scrollTo({ top: 0, behavior: "smooth" }); }} className={`${T.outline} !px-4 !py-2`}>Modifier</button>
              <button data-testid={`offre-delete-${i}`} onClick={async () => { if (window.confirm("Supprimer cette offre ?")) { await axios.delete(`${API}/admin/offres/${o.id}`, { headers: authHeaders() }); load(); } }} className={`${T.outline} !px-4 !py-2 !border-red-300 !text-red-700 dark:!text-red-400`}>Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Paramètres / Paiements ----------
function SettingsTab({ mode }) {
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: authHeaders() }).then((r) => setForm(r.data)).catch(() => {});
  }, []);
  if (!form) return <p className="text-stone dark:text-mist">Chargement…</p>;

  const setVir = (k, v) => setForm({ ...form, virement: { ...(form.virement || {}), [k]: v } });
  const setSoc = (k, v) => setForm({ ...form, socials: { ...(form.socials || {}), [k]: v } });

  const save = async (e) => {
    e.preventDefault();
    await axios.put(`${API}/admin/settings`, form, { headers: authHeaders() });
    setMsg("Paramètres enregistrés.");
    setTimeout(() => setMsg(""), 2500);
  };

  const isPaiements = mode === "paiements";
  return (
    <form data-testid={`admin-${mode}`} onSubmit={save} className={`${T.card} space-y-4 max-w-3xl`}>
      <p className="font-serif text-xl text-ink dark:text-whisper">{isPaiements ? "Paiements" : "Paramètres"}</p>
      {isPaiements ? (
        <>
          <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Lien Tidycal global</span>
            <input data-testid="set-tidycal" className={T.input} value={form.tidycal_url || ""} onChange={(e) => setForm({ ...form, tidycal_url: e.target.value })} /></label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid="set-virement-active" type="checkbox" checked={!!form.virement?.active} onChange={(e) => setVir("active", e.target.checked)} className="w-4 h-4 accent-[#2d4a3e]" />
            <span className="text-sm text-ink dark:text-whisper">Accepter le virement bancaire (affiche l'IBAN sur les pages ville)</span>
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Titulaire</span>
              <input data-testid="set-titulaire" className={T.input} value={form.virement?.titulaire || ""} onChange={(e) => setVir("titulaire", e.target.value)} /></label>
            <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">IBAN</span>
              <input data-testid="set-iban" className={T.input} value={form.virement?.iban || ""} onChange={(e) => setVir("iban", e.target.value)} /></label>
            <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">BIC</span>
              <input data-testid="set-bic" className={T.input} value={form.virement?.bic || ""} onChange={(e) => setVir("bic", e.target.value)} /></label>
            <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Banque</span>
              <input data-testid="set-banque" className={T.input} value={form.virement?.banque || ""} onChange={(e) => setVir("banque", e.target.value)} /></label>
            <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Référence virement</span>
              <input data-testid="set-reference" className={T.input} value={form.virement?.reference || ""} onChange={(e) => setVir("reference", e.target.value)} /></label>
          </div>
          <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Instructions virement</span>
            <textarea data-testid="set-instructions" rows={3} className={T.input} value={form.virement?.instructions || ""} onChange={(e) => setVir("instructions", e.target.value)} /></label>
        </>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Email de notification admin</span>
              <input data-testid="set-email" type="email" className={T.input} value={form.email_contact || ""} onChange={(e) => setForm({ ...form, email_contact: e.target.value })} /></label>
            <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Téléphone</span>
              <input data-testid="set-telephone" className={T.input} value={form.telephone || ""} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></label>
            <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Seuil de confirmation d'une ville (intéressés)</span>
              <input data-testid="set-seuil" type="number" min="1" className={T.input} value={form.seuil_confirmation ?? 15} onChange={(e) => setForm({ ...form, seuil_confirmation: Number(e.target.value) || 15 })} /></label>
            {["instagram", "tiktok", "youtube", "facebook"].map((k) => (
              <label key={k} className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">{k}</span>
                <input data-testid={`set-social-${k}`} className={T.input} value={form.socials?.[k] || ""} onChange={(e) => setSoc(k, e.target.value)} /></label>
            ))}
          </div>
          <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Texte consentement RGPD</span>
            <textarea data-testid="set-rgpd" rows={2} className={T.input} value={form.rgpd_text || ""} onChange={(e) => setForm({ ...form, rgpd_text: e.target.value })} /></label>
          <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Mentions légales</span>
            <textarea data-testid="set-mentions" rows={2} className={T.input} value={form.mentions_legales || ""} onChange={(e) => setForm({ ...form, mentions_legales: e.target.value })} /></label>
          <label className="block"><span className="block text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1">Confidentialité</span>
            <textarea data-testid="set-confidentialite" rows={2} className={T.input} value={form.confidentialite || ""} onChange={(e) => setForm({ ...form, confidentialite: e.target.value })} /></label>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-3 cursor-pointer"><input data-testid="set-show-demandes" type="checkbox" checked={!!form.show_demandes} onChange={(e) => setForm({ ...form, show_demandes: e.target.checked })} className="w-4 h-4 accent-[#2d4a3e]" /><span className="text-sm text-ink dark:text-whisper">Afficher « Villes demandées »</span></label>
            <label className="flex items-center gap-3 cursor-pointer"><input data-testid="set-show-videos" type="checkbox" checked={!!form.show_videos} onChange={(e) => setForm({ ...form, show_videos: e.target.checked })} className="w-4 h-4 accent-[#2d4a3e]" /><span className="text-sm text-ink dark:text-whisper">Afficher les vidéos</span></label>
            <label className="flex items-center gap-3 cursor-pointer"><input data-testid="set-banner" type="checkbox" checked={!!form.banner_enabled} onChange={(e) => setForm({ ...form, banner_enabled: e.target.checked })} className="w-4 h-4 accent-[#2d4a3e]" /><span className="text-sm text-ink dark:text-whisper">Bannière des villes active</span></label>
            <label className="flex items-center gap-3"><span className="text-sm text-ink dark:text-whisper">Position bannière :</span>
              <select data-testid="set-banner-position" className={`${T.input} !w-32 !py-2`} value={form.banner_position || "haut"} onChange={(e) => setForm({ ...form, banner_position: e.target.value })}>
                <option value="haut">Haut</option><option value="bas">Bas</option>
              </select></label>
          </div>
        </>
      )}
      {msg && <p data-testid={`${mode}-msg`} className="text-sm text-forest dark:text-gold">{msg}</p>}
      <button data-testid={`${mode}-save`} type="submit" className={T.forest}>Enregistrer</button>
    </form>
  );
}

// ---------- Programme / Vidéos / Témoignages / FAQ configs ----------
const PROGRAMME_FIELDS = [
  { name: "heure", label: "Heure (ex : 9h30)" }, { name: "titre", label: "Titre" },
  { name: "description", label: "Description", type: "textarea", rows: 2 },
  { name: "ordre", label: "Ordre", type: "number", default: "1" },
];
const VIDEO_FIELDS = [
  { name: "titre", label: "Titre" }, { name: "youtube_url", label: "URL YouTube", type: "url" },
  { name: "description", label: "Description", type: "textarea", rows: 2 },
  { name: "ordre", label: "Ordre", type: "number", default: "1" },
  { name: "active", label: "Visible", type: "checkbox", default: true },
];
const TEMOIGNAGE_FIELDS = [
  { name: "texte", label: "Témoignage", type: "textarea" }, { name: "auteur", label: "Auteur" },
  { name: "ville", label: "Ville de l'auteur" }, { name: "photo", label: "Photo (URL, optionnel)", type: "url" },
  { name: "note", label: "Note (1-5)", type: "number", default: "5" },
  { name: "active", label: "Visible", type: "checkbox", default: true },
];
const FAQ_FIELDS = [
  { name: "question", label: "Question" }, { name: "reponse", label: "Réponse", type: "textarea" },
  { name: "ordre", label: "Ordre", type: "number", default: "1" },
  { name: "active", label: "Visible", type: "checkbox", default: true },
];

// ---------- Calendrier provisoire (page /tournee) ----------
const CALENDRIER_FIELDS = [
  { name: "mois", label: "Mois (ex : Janvier 2027)" },
  { name: "ville", label: "Ville" },
  { name: "ordre", label: "Ordre d'affichage", type: "number", default: "1" },
  { name: "active", label: "Visible", type: "checkbox", default: true },
];
const emptyCalendrier = () => ({ ...emptyForm(CALENDRIER_FIELDS), active: true });

function CalendrierTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyCalendrier());
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const load = useCallback(() => {
    axios.get(`${API}/admin/calendrier`, { headers: authHeaders() }).then((r) => setItems(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    const payload = { ...form, ordre: Number(form.ordre) || 0 };
    try {
      if (editId) await axios.put(`${API}/admin/calendrier/${editId}`, payload, { headers: authHeaders() });
      else await axios.post(`${API}/admin/calendrier`, payload, { headers: authHeaders() });
      setForm(emptyCalendrier()); setEditId(null); load();
      setMsg({ type: "ok", text: editId ? "Modifié." : "Ajouté." });
    } catch (e2) {
      setMsg({ type: "err", text: fmtErr(e2) });
    }
  };

  return (
    <div data-testid="admin-calendrier" className="space-y-6">
      <p className="text-sm text-stone dark:text-mist leading-relaxed">
        Le calendrier provisoire affiché sur la page /tournee : une ligne par ville, avec son mois et son ordre d'affichage.
        Les villes sont regroupées par mois dans l'ordre croissant — veillez à ce que les lignes d'un même mois se suivent.
      </p>
      <form onSubmit={save} className={`${T.card} space-y-4`}>
        <p className="font-serif text-xl text-ink dark:text-whisper">{editId ? "Modifier" : "Ajouter"} — une ville du calendrier</p>
        <FieldGrid fields={CALENDRIER_FIELDS} form={form} setForm={setForm} testidPrefix="admin-calendrier-field" />
        {msg.text && <p data-testid="admin-calendrier-msg" className={`text-sm ${msg.type === "err" ? "text-red-700 dark:text-red-400" : "text-forest dark:text-gold"}`}>{msg.text}</p>}
        <div className="flex gap-3">
          <button data-testid="admin-calendrier-save" type="submit" className={T.forest}>{editId ? "Enregistrer" : "Ajouter"}</button>
          {editId && <button type="button" data-testid="admin-calendrier-cancel" onClick={() => { setEditId(null); setForm(emptyCalendrier()); }} className={T.outline}>Annuler</button>}
        </div>
      </form>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-stone dark:text-mist">Aucune ville au calendrier.</p>}
        {items.map((it, i) => (
          <div key={it.id} data-testid={`admin-calendrier-row-${i}`} className={`${T.card} !p-4 flex flex-wrap items-center justify-between gap-3 ${it.active === false ? "opacity-50" : ""}`}>
            <p className="font-medium text-ink dark:text-whisper">{it.mois} — {it.ville} <span className="text-xs text-stone dark:text-mist">· ordre {it.ordre}</span></p>
            <div className="flex gap-2">
              <button data-testid={`admin-calendrier-toggle-${i}`} onClick={async () => { await axios.put(`${API}/admin/calendrier/${it.id}`, { ...it, active: it.active === false }, { headers: authHeaders() }); load(); }} className={`${T.outline} !px-4 !py-2`}>{it.active === false ? "Activer" : "Masquer"}</button>
              <button data-testid={`admin-calendrier-edit-${i}`} onClick={() => { setEditId(it.id); setForm({ ...emptyCalendrier(), ...it }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className={`${T.outline} !px-4 !py-2`}>Modifier</button>
              <button data-testid={`admin-calendrier-delete-${i}`} onClick={async () => { if (window.confirm("Supprimer définitivement ?")) { await axios.delete(`${API}/admin/calendrier/${it.id}`, { headers: authHeaders() }); load(); } }} className={`${T.outline} !px-4 !py-2 !border-red-300 !text-red-700 dark:!text-red-400`}>Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Coquille admin ----------
const TABS = [
  ["dashboard", "Tableau de bord"], ["demandes", "Demandes de villes"], ["lieux", "Lieux proposés"],
  ["inscriptions", "Inscriptions"], ["villes", "Villes"], ["calendrier", "Calendrier"], ["offres", "Offres"],
  ["programme", "Programme"], ["videos", "Vidéos"], ["temoignages", "Témoignages"],
  ["faq", "FAQ"], ["paiements", "Paiements"], ["parametres", "Paramètres"],
];

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers: authHeaders() }).then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return <p className="text-stone dark:text-mist">Chargement…</p>;
  const cards = [
    ["Villes actives", stats.villes_actives], ["Demandes de villes", stats.demandes],
    ["Lieux proposés", stats.lieux], ["Inscriptions (emails)", stats.inscriptions],
    ["Revenus estimés", `${stats.revenus_estimes} €`],
  ];
  return (
    <div data-testid="admin-dashboard" className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map(([label, value], i) => (
        <div key={label} data-testid={`dash-card-${i}`} className={`${T.card} !p-5 text-center`}>
          <p className="font-serif text-3xl text-forest dark:text-gold">{value}</p>
          <p className="text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist mt-1">{label}</p>
        </div>
      ))}
    </div>
  );
}

export default function TourneeAdmin() {
  const [token, setTok] = useState(getToken());
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => { setMeta("Administration — La Tournée Le Guérisseur", "Back-office de la tournée."); }, []);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/me`, { headers: authHeaders() })
      .then((r) => setMe(r.data))
      .catch(() => { clearToken(); setTok(""); setMe(null); });
  }, [token]);

  if (!token || !me) return <Login onOk={(t) => setTok(t)} />;

  return (
    <div data-testid="tournee-admin" className="min-h-screen bg-ivory/80 dark:bg-night/80">
      <header className="sticky top-0 z-40 bg-ivory/90 dark:bg-night/90 backdrop-blur border-b border-beige dark:border-charcoal">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <p className="font-serif text-xl text-ink dark:text-whisper">La Tournée <span className="text-gold text-xs tracking-[0.2em] uppercase">· Admin</span></p>
          <div className="flex items-center gap-4 text-xs">
            <a data-testid="admin-view-site" href="/tournee" target="_blank" rel="noopener noreferrer" className="text-stone dark:text-mist hover:text-gold">Voir la page</a>
            <span className="text-stone dark:text-mist hidden sm:inline">{me.email}</span>
            <button data-testid="admin-logout" onClick={() => { clearToken(); setTok(""); setMe(null); }} className="text-red-700 dark:text-red-400 hover:underline">Déconnexion</button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-5 py-8">
        <nav data-testid="admin-tabs" className="flex flex-wrap gap-2 mb-8">
          {TABS.map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`${T.adminTab} ${tab === key ? "bg-forest text-ivory" : "border border-beige dark:border-charcoal text-stone dark:text-mist hover:border-gold"}`}>
              {label}
            </button>
          ))}
        </nav>
        {tab === "dashboard" && <Dashboard />}
        {tab === "demandes" && <ListTab path="demandes" csvName="tournee-demandes.csv" testid="admin-demandes" />}
        {tab === "lieux" && <ListTab path="lieux" csvName="tournee-lieux.csv" withNotes testid="admin-lieux" />}
        {tab === "inscriptions" && <ListTab path="inscriptions" csvName="tournee-inscriptions.csv" testid="admin-inscriptions" />}
        {tab === "villes" && <VillesTab />}
        {tab === "calendrier" && <CalendrierTab />}
        {tab === "offres" && <OffresTab />}
        {tab === "programme" && <CrudTab path="programme" title="un moment du dimanche" fields={PROGRAMME_FIELDS} testid="admin-programme" />}
        {tab === "videos" && <CrudTab path="videos" title="une vidéo (max 3 par ville)" fields={VIDEO_FIELDS} withGlobal testid="admin-videos" />}
        {tab === "temoignages" && <CrudTab path="temoignages" title="un témoignage" fields={TEMOIGNAGE_FIELDS} withGlobal testid="admin-temoignages" />}
        {tab === "faq" && <CrudTab path="faq" title="une question" fields={FAQ_FIELDS} withGlobal testid="admin-faq" />}
        {tab === "paiements" && <SettingsTab mode="paiements" />}
        {tab === "parametres" && <SettingsTab mode="parametres" />}
      </div>
    </div>
  );
}
